import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import type { BillingFeature } from 'webapp/enums/enums';

import type { TrackingOriginTypeForTrialPopupOpened } from 'webapp/services/billing';

export interface Args {
	text?: string;
	feature?: BillingFeature;
	size?: string;
	theme?: string;
	// eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
	trackingOrigin?: string | TrackingOriginTypeForTrialPopupOpened | null;
	skipPopup?: boolean;
	useTargetPlan?: boolean;
	onSuccess?: () => void;
	onTrigger?: () => void;
}

export default abstract class CommonRequestTrialBaseComponent<T extends Args> extends Component<T> {
	@service('billing') declare billingService: Service.Billing;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('flow/promotions')
	declare flowPromotionsService: Service.Flow.Promotions;

	@tracked isRequestTrialInProgress = false;

	get targetPlan() {
		const { feature } = this.args;
		return feature
			? this.billingService.getNextPlanForFeatureWithSameBillingCycle(feature)
			: null;
	}

	get text(): string {
		if (this.args.text) {
			return this.args.text;
		} else if (this.billingService.isActiveTrial) {
			const prefix = 'Change trial';
			if (this.args.useTargetPlan) {
				return `${prefix} to ‘${this.targetPlan?.name}’`;
			} else {
				return `${prefix} plan`;
			}
		} else {
			return 'Try 14 days for free';
		}
	}
	@action
	requestTrial() {
		const { feature } = this.args;

		if (feature && this.billingService.checkForPermissions(feature)) {
			const { trackingOrigin, skipPopup, onSuccess } = this.args,
				targetPlan = this.targetPlan;

			const targetPlanType = targetPlan?.planType,
				payingTrialPromotions = targetPlanType
					? this.flowPromotionsService.getHigherPlanAutomatedTrialOptionPromotionsForPlanType(
							targetPlanType,
						)
					: [];

			if (targetPlan && payingTrialPromotions.length) {
				// Eligible for paying trial
				this.flowPromotionsService.openStartHigherPlanAutomatedTrialPopup(
					targetPlan,
					feature,
				);
			} else {
				if (!this.isRequestTrialInProgress) {
					this.isRequestTrialInProgress = true;

					void this.billingService
						.requestTrial(
							feature,
							targetPlan,
							trackingOrigin as TrackingOriginTypeForTrialPopupOpened,
							skipPopup,
						)
						.then(() => {
							if (onSuccess) {
								onSuccess();
							}
						})
						.finally(() => {
							this.isRequestTrialInProgress = false;
						});
				}
			}
		}

		if (this.args.onTrigger) {
			this.args.onTrigger();
		}
	}
}
